/**
 * FastTrackFlow — pełny "URL → reklama gotowa do odpalenia" wizard.
 *
 * Pięć kroków:
 *  1. URL → /express-init (scrape + offer sheet + suggestedGoals + productImageUrls)
 *  2. Akceptacja arkusza ofertowego (preview + edit)
 *  3. Cel + KPI (pre-filled z suggestedGoals)
 *  4. Struktura kampanii (suggest z LLM, user akceptuje lub zmienia)
 *  5. Źródło zdjęć: scraped / AI-generate / upload własny
 *
 * Po finalize'ie wraca do parenta przez onComplete(draftId, conversationId)
 * — parent przełącza UI w klasyczny CampaignBuilder z załadowanym draftem.
 */
import React, { useState, useCallback, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { apiRequest, uploadFile } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Rocket,
  Loader2,
  Globe,
  Sparkles,
  Upload,
  ImagePlus,
  CheckCircle2,
  ArrowRight,
  ArrowLeft,
  X,
  FileText,
  Wand2,
} from "lucide-react";

type BusinessModel = "ecommerce_direct" | "consultation_booking" | "lead_collection";
type ImageSource = "scraped" | "ai_generate" | "user_upload";

interface ExpressInitResponse {
  offerSheetId: string | null;
  offerSheetContent: string;
  businessModel: BusinessModel;
  suggestedGoals: {
    businessModel: BusinessModel;
    monthlyBudget: number;
    targetRoas?: number;
    targetCpaMin?: number;
    targetCpaMax?: number;
    targetCplMin?: number;
    targetCplMax?: number;
  };
  productImageUrls: string[];
  scraped: { name: string; description: string };
}

interface SuggestStructureResponse {
  adSets: number;
  adsPerAdSet: number;
  totalAds: number;
  rationale: string;
  budgetType: "ABO" | "CBO";
}

interface UploadedAsset {
  id: string;
  url: string;
  type: "image" | "video";
  name: string;
  category: "product" | "avatar" | "reference";
}

export interface FastTrackFlowProps {
  /** When set, the wizard finalizes the draft against this conversation. Otherwise creates a new one. */
  existingConversationId?: string;
  clientId?: string | null;
  onComplete: (result: { draftId: string; conversationId: string }) => void;
  onCancel: () => void;
}

type WizardStep = "url" | "offer" | "goals" | "structure" | "images" | "submitting";

const STEP_ORDER: WizardStep[] = ["url", "offer", "goals", "structure", "images"];

function Stepper({ active }: { active: WizardStep }): JSX.Element {
  const idx = STEP_ORDER.indexOf(active);
  return (
    <div className="flex items-center gap-2">
      {STEP_ORDER.map((s, i) => {
        const done = i < idx;
        const cur = i === idx;
        return (
          <div key={s} className="flex items-center gap-2">
            <div
              className={`w-7 h-7 rounded-full text-xs font-semibold flex items-center justify-center border ${
                done
                  ? "bg-emerald-500/20 border-emerald-500/60 text-emerald-300"
                  : cur
                    ? "bg-orange-500/20 border-orange-500/60 text-orange-300"
                    : "bg-zinc-800/40 border-zinc-700 text-zinc-500"
              }`}
            >
              {done ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
            </div>
            {i < STEP_ORDER.length - 1 && <div className="w-6 h-px bg-zinc-700" />}
          </div>
        );
      })}
    </div>
  );
}

export default function FastTrackFlow({
  existingConversationId,
  clientId,
  onComplete,
  onCancel,
}: FastTrackFlowProps): JSX.Element {
  const { t } = useTranslation();
  const { toast } = useToast();

  const [step, setStep] = useState<WizardStep>("url");
  const [url, setUrl] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [init, setInit] = useState<ExpressInitResponse | null>(null);
  const [offerSheetText, setOfferSheetText] = useState("");

  // Offer sheet refine chat
  const [refineInstruction, setRefineInstruction] = useState("");
  const [refining, setRefining] = useState(false);

  const handleRefineOffer = useCallback(async (): Promise<void> => {
    const instr = refineInstruction.trim();
    if (!instr || !init) return;
    setRefining(true);
    try {
      const data = (await apiRequest("POST", "/api/campaign-creator/fast-track/refine-offer", {
        offerSheetText,
        instruction: instr,
        offerSheetId: init.offerSheetId ?? undefined,
      })) as { offerSheetText: string };
      setOfferSheetText(data.offerSheetText);
      setRefineInstruction("");
      toast({
        title: "Arkusz zaktualizowany",
        description: "AI poprawiło arkusz zgodnie z prośbą.",
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Refine failed";
      toast({ title: "Nie udało się poprawić arkusza", description: msg, variant: "destructive" });
    } finally {
      setRefining(false);
    }
  }, [refineInstruction, offerSheetText, init, toast]);

  // Goals state (pre-filled after analyze)
  const [objective, setObjective] = useState<string>("leads");
  const [monthlyBudget, setMonthlyBudget] = useState<number>(1500);
  const [targetCplMin, setTargetCplMin] = useState<number | "">("");
  const [targetCplMax, setTargetCplMax] = useState<number | "">("");
  const [targetCpaMin, setTargetCpaMin] = useState<number | "">("");
  const [targetCpaMax, setTargetCpaMax] = useState<number | "">("");
  const [targetRoas, setTargetRoas] = useState<number | "">("");

  // Structure state
  const [suggesting, setSuggesting] = useState(false);
  const [structure, setStructure] = useState<SuggestStructureResponse | null>(null);

  // Images state
  const [imageSource, setImageSource] = useState<ImageSource | null>(null);

  // Idiotoodporność: gdy scrape nie zwrócił żadnych grafik, domyślnie kierujemy
  // usera na wgranie własnych (zamiast zostawiać go bez wybranego źródła).
  useEffect(() => {
    if (init && init.productImageUrls.length === 0 && imageSource === null) {
      setImageSource("user_upload");
    }
  }, [init, imageSource]);
  const [uploadedAssets, setUploadedAssets] = useState<UploadedAsset[]>([]);
  const [uploadingCategory, setUploadingCategory] = useState<UploadedAsset["category"] | null>(
    null
  );
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const pendingCategoryRef = useRef<UploadedAsset["category"]>("product");

  const goBack = useCallback(() => {
    const i = STEP_ORDER.indexOf(step);
    if (i <= 0) {
      onCancel();
      return;
    }
    setStep(STEP_ORDER[i - 1]);
  }, [step, onCancel]);

  // ---------- Step 1: URL → analyze ----------
  const handleAnalyze = async (): Promise<void> => {
    const trimmed = url.trim();
    if (!/^https?:\/\//i.test(trimmed)) {
      toast({ title: t("fastTrack.errors.invalidUrl"), variant: "destructive" });
      return;
    }
    setAnalyzing(true);
    try {
      const data = (await apiRequest("POST", "/api/campaign-creator/express-init", {
        url: trimmed,
        clientId: clientId ?? undefined,
      })) as ExpressInitResponse;
      setInit(data);
      setOfferSheetText(data.offerSheetContent);
      // Pre-fill goals from suggestion
      setMonthlyBudget(data.suggestedGoals.monthlyBudget);
      if (data.businessModel === "ecommerce_direct") setObjective("sales");
      else setObjective("leads");
      setTargetCplMin(data.suggestedGoals.targetCplMin ?? "");
      setTargetCplMax(data.suggestedGoals.targetCplMax ?? "");
      setTargetCpaMin(data.suggestedGoals.targetCpaMin ?? "");
      setTargetCpaMax(data.suggestedGoals.targetCpaMax ?? "");
      setTargetRoas(data.suggestedGoals.targetRoas ?? "");
      setStep("offer");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Analyze failed";
      toast({
        title: t("fastTrack.errors.analyzeFailed"),
        description: msg,
        variant: "destructive",
      });
    } finally {
      setAnalyzing(false);
    }
  };

  // ---------- Step 4: Suggest structure ----------
  const handleSuggestStructure = async (): Promise<void> => {
    if (!init) return;
    setSuggesting(true);
    try {
      const data = (await apiRequest("POST", "/api/campaign-creator/fast-track/suggest-structure", {
        businessModel: init.businessModel,
        monthlyBudget,
        offerSummary: offerSheetText.slice(0, 2000),
      })) as SuggestStructureResponse;
      setStructure(data);
    } catch (err) {
      // Fallback locally if endpoint fails
      setStructure({
        adSets: monthlyBudget < 2000 ? 1 : monthlyBudget < 6000 ? 2 : 3,
        adsPerAdSet: 3,
        totalAds: monthlyBudget < 2000 ? 3 : monthlyBudget < 6000 ? 6 : 9,
        rationale: t("fastTrack.structure.fallback"),
        budgetType: "ABO",
      });
    } finally {
      setSuggesting(false);
    }
  };

  const goToStructure = async (): Promise<void> => {
    setStep("structure");
    if (!structure) await handleSuggestStructure();
  };

  // ---------- Step 5: Images ----------
  const triggerUpload = (category: UploadedAsset["category"]): void => {
    pendingCategoryRef.current = category;
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const category = pendingCategoryRef.current;
    setUploadingCategory(category);
    try {
      const newAssets: UploadedAsset[] = [];
      for (const file of Array.from(files)) {
        const isVideo = file.type.startsWith("video/");
        const { uploadUrl, getUrl, key } = (await apiRequest(
          "POST",
          "/api/campaign-creator/r2-presign",
          {
            filename: file.name,
            mimeType: file.type || (isVideo ? "video/mp4" : "image/jpeg"),
          }
        )) as { uploadUrl: string; getUrl: string; key: string };

        let finalUrl = "";
        try {
          const putRes = await fetch(uploadUrl, {
            method: "PUT",
            body: file,
            headers: { "Content-Type": file.type || (isVideo ? "video/mp4" : "image/jpeg") },
          });
          if (putRes.ok) finalUrl = getUrl;
        } catch {
          // fallthrough to proxy
        }
        if (!finalUrl) {
          const formData = new FormData();
          formData.append("file", file);
          const result = (await uploadFile("/api/campaign-creator/r2-upload", formData)) as {
            url: string;
            r2Key: string;
          };
          finalUrl = result.url;
        }
        newAssets.push({
          id: `${key}-${Date.now()}`,
          url: finalUrl,
          type: isVideo ? "video" : "image",
          name: file.name,
          category,
        });
      }
      setUploadedAssets((prev) => [...prev, ...newAssets]);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Upload failed";
      toast({
        title: t("fastTrack.errors.uploadFailed"),
        description: msg,
        variant: "destructive",
      });
    } finally {
      setUploadingCategory(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // Paste-from-clipboard support
  const handlePaste = useCallback(
    async (category: UploadedAsset["category"], e: React.ClipboardEvent): Promise<void> => {
      const items = Array.from(e.clipboardData?.items ?? []);
      const imageItems = items.filter((it) => it.type.startsWith("image/"));
      if (imageItems.length === 0) return;
      e.preventDefault();
      setUploadingCategory(category);
      try {
        const newAssets: UploadedAsset[] = [];
        for (const item of imageItems) {
          const file = item.getAsFile();
          if (!file) continue;
          const filename = `pasted-${Date.now()}.png`;
          const { uploadUrl, getUrl, key } = (await apiRequest(
            "POST",
            "/api/campaign-creator/r2-presign",
            {
              filename,
              mimeType: file.type || "image/png",
            }
          )) as { uploadUrl: string; getUrl: string; key: string };
          let finalUrl = "";
          try {
            const putRes = await fetch(uploadUrl, {
              method: "PUT",
              body: file,
              headers: { "Content-Type": file.type || "image/png" },
            });
            if (putRes.ok) finalUrl = getUrl;
          } catch {
            /* fall through */
          }
          if (!finalUrl) {
            const formData = new FormData();
            formData.append("file", new File([file], filename, { type: file.type || "image/png" }));
            const result = (await uploadFile("/api/campaign-creator/r2-upload", formData)) as {
              url: string;
              r2Key: string;
            };
            finalUrl = result.url;
          }
          newAssets.push({
            id: `${key}-${Date.now()}`,
            url: finalUrl,
            type: "image",
            name: filename,
            category,
          });
        }
        setUploadedAssets((prev) => [...prev, ...newAssets]);
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Paste failed";
        toast({
          title: t("fastTrack.errors.uploadFailed"),
          description: msg,
          variant: "destructive",
        });
      } finally {
        setUploadingCategory(null);
      }
    },
    [toast, t]
  );

  const removeAsset = (id: string): void =>
    setUploadedAssets((prev) => prev.filter((a) => a.id !== id));

  // ---------- Finalize ----------
  const handleFinalize = async (): Promise<void> => {
    if (!init || !structure || !imageSource) return;
    setStep("submitting");
    try {
      // Make sure we have a conversation
      let conversationId = existingConversationId ?? "";
      if (!conversationId) {
        const conv = (await apiRequest("POST", "/api/campaign-creator/conversations", {
          title: init.scraped.name?.slice(0, 80) || "Fast Track",
        })) as { id: string };
        conversationId = conv.id;
      }

      let creativeUrls: Array<{ url: string; type: "image" | "video"; name?: string }> = [];
      if (imageSource === "scraped") {
        creativeUrls = init.productImageUrls.map((u, i) => ({
          url: u,
          type: "image" as const,
          name: `scraped-${i + 1}`,
        }));
      } else if (imageSource === "user_upload") {
        creativeUrls = uploadedAssets.map((a) => ({ url: a.url, type: a.type, name: a.name }));
      } else {
        // ai_generate: leave empty, downstream pipeline will generate per ad
        creativeUrls = [];
      }

      const result = (await apiRequest("POST", "/api/campaign-creator/fast-track/finalize", {
        conversationId,
        businessModel: init.businessModel,
        monthlyBudget,
        objective,
        targetCpl:
          targetCplMin || targetCplMax
            ? { min: targetCplMin || undefined, max: targetCplMax || undefined }
            : undefined,
        targetCpa:
          targetCpaMin || targetCpaMax
            ? { min: targetCpaMin || undefined, max: targetCpaMax || undefined }
            : undefined,
        targetRoas: targetRoas === "" ? undefined : Number(targetRoas),
        structure,
        imageSource,
        creativeUrls,
        campaignName: init.scraped.name?.slice(0, 80),
        landingPageUrl: url,
        offerSheetId: init.offerSheetId ?? undefined,
        clientId: clientId ?? undefined,
      })) as { draftId: string };

      onComplete({ draftId: result.draftId, conversationId });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Finalize failed";
      toast({
        title: t("fastTrack.errors.finalizeFailed"),
        description: msg,
        variant: "destructive",
      });
      setStep("images");
    }
  };

  // ---------- Render ----------
  return (
    <div className="flex flex-col min-h-0 h-full bg-[#06070a] text-zinc-200">
      <div className="px-6 pt-6 pb-4 border-b border-zinc-800/60">
        <div className="flex items-center justify-between max-w-3xl mx-auto">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center">
              <Rocket className="w-4 h-4 text-white" />
            </div>
            <div>
              <div className="text-base font-semibold">{t("fastTrack.title")}</div>
              <div className="text-xs text-zinc-500">{t("fastTrack.subtitle")}</div>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onCancel}>
            {t("fastTrack.exit")}
          </Button>
        </div>
        <div className="max-w-3xl mx-auto mt-4">
          <Stepper active={step === "submitting" ? "images" : step} />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-6 py-8 space-y-6">
          {step === "url" && (
            <section className="space-y-4">
              <h2 className="text-xl font-semibold">{t("fastTrack.url.heading")}</h2>
              <p className="text-sm text-zinc-400">{t("fastTrack.url.description")}</p>
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="https://twojsklep.pl/produkt"
                    className="pl-9 bg-zinc-900/60 border-zinc-700"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !analyzing) handleAnalyze();
                    }}
                  />
                </div>
                <Button onClick={handleAnalyze} disabled={analyzing || !url.trim()}>
                  {analyzing ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4 mr-1" />
                  )}
                  {analyzing ? t("fastTrack.url.analyzing") : t("fastTrack.url.analyze")}
                </Button>
              </div>
            </section>
          )}

          {step === "offer" && init && (
            <section className="space-y-4">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <FileText className="w-5 h-5 text-orange-400" />
                {t("fastTrack.offer.heading")}
              </h2>
              <p className="text-sm text-zinc-400">{t("fastTrack.offer.description")}</p>
              <Textarea
                value={offerSheetText}
                onChange={(e) => setOfferSheetText(e.target.value)}
                rows={16}
                className="bg-zinc-900/60 border-zinc-700 font-mono text-xs"
              />
              <div className="text-xs text-zinc-500">
                {t("fastTrack.offer.scrapedImages", { count: init.productImageUrls.length })}
              </div>

              {/* AI refine chat */}
              <div className="rounded-lg border border-orange-500/20 bg-orange-500/5 p-3 space-y-2">
                <div className="flex items-center gap-2 text-xs text-orange-300">
                  <Sparkles className="w-3.5 h-3.5" />
                  <span className="font-medium">Popraw przez AI</span>
                  <span className="text-zinc-500">— napisz co zmienić, AI przepisze arkusz</span>
                </div>
                <div className="flex gap-2">
                  <Input
                    value={refineInstruction}
                    onChange={(e) => setRefineInstruction(e.target.value)}
                    placeholder='np. "Dodaj sekcję z USP" albo "Skróć opis o połowę i dodaj cenę 199 zł"'
                    className="bg-zinc-900/60 border-zinc-700 text-sm"
                    disabled={refining}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey && !refining) {
                        e.preventDefault();
                        handleRefineOffer();
                      }
                    }}
                  />
                  <Button
                    onClick={handleRefineOffer}
                    disabled={refining || !refineInstruction.trim()}
                    size="sm"
                  >
                    {refining ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Wand2 className="w-4 h-4" />
                    )}
                    <span className="ml-1.5">{refining ? "Poprawiam…" : "Popraw"}</span>
                  </Button>
                </div>
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {[
                    "Skróć całość o połowę",
                    "Dodaj sekcję USP (3 punkty)",
                    "Wyróżnij grupę docelową",
                    "Dopisz typowe obiekcje klienta",
                  ].map((suggestion) => (
                    <button
                      key={suggestion}
                      type="button"
                      onClick={() => setRefineInstruction(suggestion)}
                      disabled={refining}
                      className="px-2 py-0.5 rounded-md text-[11px] bg-zinc-800/60 border border-zinc-700 text-zinc-400 hover:text-zinc-200 hover:border-zinc-600 disabled:opacity-50"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            </section>
          )}

          {step === "goals" && init && (
            <section className="space-y-4">
              <h2 className="text-xl font-semibold">{t("fastTrack.goals.heading")}</h2>
              <p className="text-sm text-zinc-400">{t("fastTrack.goals.description")}</p>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>{t("fastTrack.goals.objective")}</Label>
                  <select
                    value={objective}
                    onChange={(e) => setObjective(e.target.value)}
                    className="w-full h-10 px-3 rounded-md bg-zinc-900/60 border border-zinc-700 text-sm"
                  >
                    <option value="leads">{t("fastTrack.goals.objectiveLeads")}</option>
                    <option value="sales">{t("fastTrack.goals.objectiveSales")}</option>
                    <option value="traffic">{t("fastTrack.goals.objectiveTraffic")}</option>
                    <option value="awareness">{t("fastTrack.goals.objectiveAwareness")}</option>
                  </select>
                  <p className="text-[11px] text-zinc-500 leading-snug">
                    Co ma się stać po kliknięciu reklamy?{" "}
                    <span className="text-zinc-400">Sprzedaż</span> = ktoś kupuje na stronie.{" "}
                    <span className="text-zinc-400">Leady</span> = zostawia kontakt (mail/telefon).{" "}
                    <span className="text-zinc-400">Ruch</span> = wchodzi i się rozgląda.{" "}
                    <span className="text-zinc-400">Zasięg</span> = jak najwięcej osób widzi reklamę
                    (branding).
                  </p>
                </div>
                <div className="space-y-1.5">
                  <Label>{t("fastTrack.goals.monthlyBudget")}</Label>
                  <Input
                    type="number"
                    min={100}
                    value={monthlyBudget}
                    onChange={(e) => setMonthlyBudget(Number(e.target.value) || 0)}
                    className="bg-zinc-900/60 border-zinc-700"
                  />
                  <p className="text-[11px] text-zinc-500 leading-snug">
                    Tyle wydasz przez cały miesiąc.{" "}
                    {monthlyBudget > 0 && (
                      <span className="text-zinc-400">
                        ~{Math.round(monthlyBudget / 30)} zł/dzień.
                      </span>
                    )}{" "}
                    {monthlyBudget < 600 && monthlyBudget > 0 && (
                      <span className="text-amber-400">
                        Za mało — Meta nie ma z czego się uczyć. Min. 600 zł/mc (20 zł/dzień).
                      </span>
                    )}
                  </p>
                </div>
              </div>

              {init.businessModel === "ecommerce_direct" ? (
                <div className="space-y-2">
                  <Label>{t("fastTrack.goals.targetRoas")}</Label>
                  <Input
                    type="number"
                    step={0.1}
                    value={targetRoas}
                    onChange={(e) =>
                      setTargetRoas(e.target.value === "" ? "" : Number(e.target.value))
                    }
                    placeholder="2.5"
                    className="bg-zinc-900/60 border-zinc-700"
                  />
                  <p className="text-[11px] text-zinc-500 leading-snug">
                    <span className="text-zinc-400">ROAS</span> = ile zł sprzedaży chcesz mieć z 1
                    zł reklamy. Np. ROAS <span className="text-zinc-400">2,5</span> = wydajesz 100
                    zł, masz 250 zł sprzedaży. Realne dla nowego sklepu:{" "}
                    <span className="text-zinc-400">1,5–3</span>. Mocno skalujący sklep:{" "}
                    <span className="text-zinc-400">3–5</span>. Zostaw puste, jeśli nie wiesz — Meta
                    i tak będzie dążyć do max sprzedaży.
                  </p>
                </div>
              ) : (
                <div className="space-y-1.5">
                  <Label>Docelowy koszt pozyskania leada (CPL)</Label>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Input
                        type="number"
                        value={targetCplMin}
                        onChange={(e) =>
                          setTargetCplMin(e.target.value === "" ? "" : Number(e.target.value))
                        }
                        placeholder="np. 15"
                        className="bg-zinc-900/60 border-zinc-700"
                      />
                      <span className="text-[10px] text-zinc-500 mt-1 block">
                        {t("fastTrack.goals.cplMin")}
                      </span>
                    </div>
                    <div>
                      <Input
                        type="number"
                        value={targetCplMax}
                        onChange={(e) =>
                          setTargetCplMax(e.target.value === "" ? "" : Number(e.target.value))
                        }
                        placeholder="np. 50"
                        className="bg-zinc-900/60 border-zinc-700"
                      />
                      <span className="text-[10px] text-zinc-500 mt-1 block">
                        {t("fastTrack.goals.cplMax")}
                      </span>
                    </div>
                  </div>
                  <p className="text-[11px] text-zinc-500 leading-snug">
                    <span className="text-zinc-400">CPL</span> = ile chcesz płacić za jeden kontakt
                    (formularz, mail, telefon). Wpisz widełki: minimum (cel optymalny) i maksimum
                    (jeszcze akceptowalny). Typowe wartości: usługi lokalne{" "}
                    <span className="text-zinc-400">10–40 zł</span>, B2B{" "}
                    <span className="text-zinc-400">50–200 zł</span>, premium{" "}
                    <span className="text-zinc-400">100–500 zł</span>. Zostaw puste, jeśli nie wiesz
                    — ustawimy dobre wartości startowe.
                  </p>
                </div>
              )}
            </section>
          )}

          {step === "structure" && (
            <section className="space-y-4">
              <h2 className="text-xl font-semibold">{t("fastTrack.structure.heading")}</h2>
              <p className="text-sm text-zinc-400">{t("fastTrack.structure.description")}</p>

              {suggesting || !structure ? (
                <div className="flex items-center gap-3 text-zinc-400 py-12 justify-center">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {t("fastTrack.structure.loading")}
                </div>
              ) : (
                <>
                  <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-zinc-400">
                        {t("fastTrack.structure.totalAds")}
                      </div>
                      <div className="text-3xl font-bold text-orange-400">{structure.totalAds}</div>
                    </div>
                    <div className="text-xs text-zinc-500 leading-relaxed">
                      {structure.rationale}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label>{t("fastTrack.structure.adSets")}</Label>
                      <Input
                        type="number"
                        min={1}
                        max={4}
                        value={structure.adSets}
                        onChange={(e) => {
                          const v = Math.max(1, Math.min(4, Number(e.target.value) || 1));
                          setStructure({
                            ...structure,
                            adSets: v,
                            totalAds: v * structure.adsPerAdSet,
                          });
                        }}
                        className="bg-zinc-900/60 border-zinc-700"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label>{t("fastTrack.structure.adsPerAdSet")}</Label>
                      <Input
                        type="number"
                        min={1}
                        max={6}
                        value={structure.adsPerAdSet}
                        onChange={(e) => {
                          const v = Math.max(1, Math.min(6, Number(e.target.value) || 1));
                          setStructure({
                            ...structure,
                            adsPerAdSet: v,
                            totalAds: structure.adSets * v,
                          });
                        }}
                        className="bg-zinc-900/60 border-zinc-700"
                      />
                    </div>
                  </div>
                </>
              )}
            </section>
          )}

          {step === "images" && init && (
            <section className="space-y-4">
              <h2 className="text-xl font-semibold">{t("fastTrack.images.heading")}</h2>
              <p className="text-sm text-zinc-400">{t("fastTrack.images.description")}</p>

              {init.productImageUrls.length === 0 && (
                <div className="rounded-lg border border-amber-500/40 bg-amber-500/[0.08] p-3 text-sm text-amber-200">
                  {t("fastTrack.images.noScrapedNote")}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {init.productImageUrls.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setImageSource("scraped")}
                    className={`text-left rounded-xl border p-4 transition ${
                      imageSource === "scraped"
                        ? "border-orange-500 bg-orange-500/10"
                        : "border-zinc-800 bg-zinc-900/40 hover:border-zinc-700"
                    }`}
                  >
                    <ImagePlus className="w-5 h-5 text-orange-400 mb-2" />
                    <div className="font-medium text-sm">{t("fastTrack.images.useScraped")}</div>
                    <div className="text-xs text-zinc-500 mt-1">
                      {t("fastTrack.images.useScrapedHint", {
                        count: init.productImageUrls.length,
                      })}
                    </div>
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setImageSource("ai_generate")}
                  className={`text-left rounded-xl border p-4 transition ${
                    imageSource === "ai_generate"
                      ? "border-orange-500 bg-orange-500/10"
                      : "border-zinc-800 bg-zinc-900/40 hover:border-zinc-700"
                  }`}
                >
                  <Wand2 className="w-5 h-5 text-orange-400 mb-2" />
                  <div className="font-medium text-sm">{t("fastTrack.images.aiGenerate")}</div>
                  <div className="text-xs text-zinc-500 mt-1">
                    {t("fastTrack.images.aiGenerateHint")}
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setImageSource("user_upload")}
                  className={`text-left rounded-xl border p-4 transition ${
                    imageSource === "user_upload"
                      ? "border-orange-500 bg-orange-500/10"
                      : "border-zinc-800 bg-zinc-900/40 hover:border-zinc-700"
                  }`}
                >
                  <Upload className="w-5 h-5 text-orange-400 mb-2" />
                  <div className="font-medium text-sm">{t("fastTrack.images.upload")}</div>
                  <div className="text-xs text-zinc-500 mt-1">
                    {t("fastTrack.images.uploadHint")}
                  </div>
                </button>
              </div>

              {imageSource === "scraped" && init.productImageUrls.length > 0 && (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-2">
                  {init.productImageUrls.map((u) => (
                    <img
                      key={u}
                      src={u}
                      alt=""
                      className="w-full aspect-square object-cover rounded-lg border border-zinc-800"
                    />
                  ))}
                </div>
              )}

              {imageSource === "user_upload" && (
                <div className="space-y-3 mt-3">
                  {(["product", "avatar", "reference"] as const).map((cat) => {
                    const items = uploadedAssets.filter((a) => a.category === cat);
                    return (
                      <div
                        key={cat}
                        className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4"
                        onPaste={(e) => handlePaste(cat, e)}
                        tabIndex={0}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <div className="text-sm font-medium">
                              {t(`fastTrack.images.cat.${cat}.label`)}
                            </div>
                            <div className="text-xs text-zinc-500">
                              {t(`fastTrack.images.cat.${cat}.hint`)}
                            </div>
                          </div>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => triggerUpload(cat)}
                            disabled={uploadingCategory === cat}
                          >
                            {uploadingCategory === cat ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <Upload className="w-4 h-4 mr-1" />
                                {t("fastTrack.images.uploadBtn")}
                              </>
                            )}
                          </Button>
                        </div>
                        {items.length > 0 && (
                          <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                            {items.map((a) => (
                              <div key={a.id} className="relative group">
                                {a.type === "image" ? (
                                  <img
                                    src={a.url}
                                    alt={a.name}
                                    className="w-full aspect-square object-cover rounded-lg border border-zinc-800"
                                  />
                                ) : (
                                  <video
                                    src={a.url}
                                    className="w-full aspect-square object-cover rounded-lg border border-zinc-800"
                                  />
                                )}
                                <button
                                  type="button"
                                  onClick={() => removeAsset(a.id)}
                                  className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-zinc-950 border border-zinc-700 flex items-center justify-center opacity-0 group-hover:opacity-100"
                                  aria-label={t("fastTrack.images.remove")}
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                        {items.length === 0 && (
                          <div className="text-xs text-zinc-600 italic">
                            {t("fastTrack.images.pasteHint")}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept="image/*,video/*"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </div>
              )}
            </section>
          )}

          {step === "submitting" && (
            <div className="flex flex-col items-center justify-center py-20 gap-3 text-zinc-400">
              <Loader2 className="w-8 h-8 animate-spin text-orange-400" />
              <div className="text-sm">{t("fastTrack.submitting")}</div>
            </div>
          )}
        </div>
      </div>

      {/* Footer / nav */}
      {step !== "submitting" && (
        <div className="border-t border-zinc-800/60 px-6 py-4">
          <div className="max-w-3xl mx-auto flex items-center justify-between">
            <Button variant="ghost" onClick={goBack}>
              <ArrowLeft className="w-4 h-4 mr-1" />
              {step === "url" ? t("fastTrack.cancel") : t("fastTrack.back")}
            </Button>
            <NextButton
              step={step}
              canProceed={canProceed({
                step,
                init,
                structure,
                imageSource,
                uploadedAssets,
                monthlyBudget,
              })}
              onNext={async () => {
                if (step === "url") return; // handled by analyze button
                if (step === "offer") return setStep("goals");
                if (step === "goals") return goToStructure();
                if (step === "structure") return setStep("images");
                if (step === "images") return handleFinalize();
              }}
              t={t}
            />
          </div>
        </div>
      )}
    </div>
  );
}

interface NextProceedArgs {
  step: WizardStep;
  init: ExpressInitResponse | null;
  structure: SuggestStructureResponse | null;
  imageSource: ImageSource | null;
  uploadedAssets: UploadedAsset[];
  monthlyBudget: number;
}

function canProceed(args: NextProceedArgs): boolean {
  switch (args.step) {
    case "url":
      return false;
    case "offer":
      return !!args.init;
    case "goals":
      return args.monthlyBudget > 0;
    case "structure":
      return !!args.structure && args.structure.adSets > 0 && args.structure.adsPerAdSet > 0;
    case "images":
      if (args.imageSource === "user_upload") return args.uploadedAssets.length > 0;
      return !!args.imageSource;
    default:
      return false;
  }
}

function NextButton({
  step,
  canProceed,
  onNext,
  t,
}: {
  step: WizardStep;
  canProceed: boolean;
  onNext: () => void | Promise<void>;
  t: ReturnType<typeof useTranslation>["t"];
}): JSX.Element | null {
  if (step === "url") return null;
  return (
    <Button onClick={() => void onNext()} disabled={!canProceed}>
      {step === "images" ? t("fastTrack.finalize") : t("fastTrack.next")}
      <ArrowRight className="w-4 h-4 ml-1" />
    </Button>
  );
}
